import moment from 'moment';
import { replaceMacrosInString } from 'js/lib/MacroReplacementUtils';
import { ParameterizedURLData } from '../types';

/**
 * Maps date/time format codes to moment.js duration units
 * Used for macro date math operations like {now+5} with date.time.unit='dd'
 */
const TIME_UNIT_MAP: { [key: string]: moment.unitOfTime.DurationConstructor } = {
  HH: 'hours',
  mm: 'minutes',
  ss: 'seconds',
  MM: 'months',
  yyyy: 'years',
  dd: 'days',
} as const;

/**
 * Normalizes date format pattern from backend format to moment.js format
 * Backend uses lowercase (yyyy, dd) while moment uses uppercase (YYYY, DD)
 *
 * @param pattern - Date format pattern (e.g., 'yyyy-MM-dd', 'MM/dd/yyyy')
 * @returns Normalized pattern for moment.js
 */
const normalizeDatePattern = (pattern: string): string => {
  return (
    pattern
      // Backend quotes literals with single quotes (e.g. yyyy-MM-dd'T'HH:mm:ss'Z');
      // moment escapes literals with square brackets. Convert 'X' -> [X] first.
      .replace(/'([^']*)'/g, '[$1]')
      .replace(/yyyy/g, 'YYYY') // Year: lowercase to uppercase
      .replace(/dd/g, 'DD') // Day: lowercase to uppercase
  );
  // MM, HH, mm, ss are the same in both formats
};

/**
 * Generates a sample URL by replacing all macros with example values
 * Handles: system macros ({now}), parent dataset macros ({path_params.id}), lookup/stage macros
 *
 * @param urlTemplate - The URL template containing macros
 * @param parameterizedURLs - Optional parameterized URL data from previous stages
 * @param parentDatasetSamples - Optional parent dataset samples for api_server flows
 * @param dateFormat - Date format pattern (e.g., 'yyyy-MM-dd', 'MM/dd/yyyy'). Defaults to 'yyyy-MM-dd'
 * @param dateTimeUnit - Time unit for operations (e.g., 'dd' for days, 'HH' for hours). Defaults to 'dd'
 * @param credentialData - Optional flat map of the bound credential's keys → values.
 *   Used to resolve `{data_credential["key"]}` macros for DISPLAY ONLY (sample URL);
 *   the saved config keeps the macro so the backend re-resolves it per execution.
 * @returns Sample URL with all macros replaced
 */
export const generateSampleURL = (
  urlTemplate: string,
  parameterizedURLs?: ParameterizedURLData | null | undefined,
  parentDatasetSamples?: any[] | undefined,
  dateFormat: string = 'yyyy-MM-dd',
  dateTimeUnit: string = 'dd',
  credentialData?: Record<string, any> | null | undefined,
): string => {
  let sampleURL = urlTemplate || '';

  // Step 1: Replace lookup/stage macros if parameterizedURLs has real data
  if (
    parameterizedURLs &&
    parameterizedURLs.urls &&
    parameterizedURLs.urls.length > 0 &&
    parameterizedURLs.urls[0].url
  ) {
    sampleURL = parameterizedURLs.urls[0].url;
  }

  if (typeof sampleURL !== 'string') {
    sampleURL = '';
  }

  // Step 2: Replace parent dataset macros (like {path_params.customerID})
  if (
    parentDatasetSamples &&
    Array.isArray(parentDatasetSamples) &&
    parentDatasetSamples.length > 0 &&
    sampleURL
  ) {
    sampleURL = replaceMacrosInString(sampleURL, parentDatasetSamples);
  }

  // Step 3: Replace system macros with 'now' ({now}, {now-1}, {now + 5}, etc.).
  // Tolerates whitespace (e.g. "{now + 1}") so typed macros evaluate too.
  sampleURL = sampleURL.replace(/\{\s*now\s*([+-]\s*\d+)?\s*\}/g, (match, offsetStr) => {
    let now = moment.utc();

    if (offsetStr) {
      const offset = parseInt(offsetStr.replace(/\s+/g, ''), 10);
      if (!Number.isNaN(offset)) {
        const unit = TIME_UNIT_MAP[dateTimeUnit] || TIME_UNIT_MAP.dd;
        now = now.add(offset, unit);
      }
    }

    // Format according to the specified dateFormat using moment
    const normalizedPattern = normalizeDatePattern(dateFormat);
    return now.format(normalizedPattern);
  });

  // Step 4: Resolve credential macros for display, e.g.
  //   {data_credential["oauth2.instance_url"]} -> https://acme.my.salesforce.com
  // Display-only (sample URL); the saved config keeps the macro so the backend binds
  // the credential at run time. Unknown keys are left as-is.
  if (credentialData) {
    sampleURL = sampleURL.replace(
      /\{\s*data_credential\s*\[\s*["']([^"']+)["']\s*\]\s*\}/g,
      (match, key) => {
        const value = credentialData[key];
        return value == null || value === '' ? match : String(value);
      },
    );
  }

  // Step 5: Leave any remaining unhandled macros as-is
  // This helps users see which macros were successfully replaced vs which remain unresolved
  return sampleURL;
};
